import React, { useState, useEffect } from "react";
import InvoiceGenerator from "../../applicationUi/components/InvoiceGenerator";
import { useDispatch } from "react-redux";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import { setCurrentInvoice } from "../../store/slices/invoiceSlice";
import { fetchInvoicesByAppointmentId } from "../../store/thunks/invoiceThunk";
import { fetchAppointmentById } from "../../store/thunks/appointmentsThunk";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";

function PayrollDetails({
  payrollId,
  appointment,
  selectedPeriod,
  selectedPeriodDates,
  submitBy,
  payBy,
  refetchInvoices,
  onRefresh,
}) {
  const [fetchedAppointment, setFetchedAppointment] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [isWideSidePanelOpen, setIsWideSidePanelOpen] = useState(false);
  const [payPercentage, setPayPercentage] = useState(0);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch appointment details
        const fetchedAppointment = await dispatch(
          fetchAppointmentById(appointment._id)
        ).unwrap();
        setFetchedAppointment(fetchedAppointment);

        // Fetch associated invoices
        const fetchedInvoices = await dispatch(
          fetchInvoicesByAppointmentId(appointment._id)
        ).unwrap();
        setInvoices(fetchedInvoices);

        // Set default pay percentage and invoice
        if (fetchedAppointment?.clinician) {
          setPayPercentage(
            fetchedAppointment.clinician.defaultPayPercentage || 45
          );
        }

        if (fetchedInvoices.length > 0) {
          setSelectedInvoice(fetchedInvoices[0]); // Use the first invoice as default
          dispatch(setCurrentInvoice(fetchedInvoices[0]));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [dispatch, appointment]);

  useEffect(() => {
    if (selectedInvoice) {
      setCalculatedAmount(
        ((selectedInvoice.amount * payPercentage) / 100).toFixed(2)
      );
    }
  }, [selectedInvoice, payPercentage]);

  const handleInvoiceClick = (invoice) => {
    setSelectedInvoice(invoice);
    dispatch(setCurrentInvoice(invoice));
    setShowInvoiceGenerator(true);
  };

  const handleAppointmentClick = () => {
    dispatch(setSelectedAppointment(appointment));
    setIsWideSidePanelOpen(true);
  };

  const handleCloseWideSidePanel = () => {
    setIsWideSidePanelOpen(false);
    onRefresh();
  };

  const isCompleted =
    appointment?.history?.some((historyItem) =>
      historyItem.status.includes("Soap Notes Completed")
    ) &&
    selectedInvoice &&
    selectedInvoice.status.toLowerCase() === "paid";

  return (
    <div className="space-y-4">
      <div className="divide-y divide-gray-100">
        <dl className="space-y-4">
          {/* Appointment Details */}
          {appointment && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Appointment Information
                </h3>
              </div>

              <div
                onClick={handleAppointmentClick}
                className="group border border-gray-200 bg-white shadow-sm divide-y divide-gray-100 cursor-pointer hover:border-gray-300 transition-colors hover:ring-2 hover:ring-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500 rounded-sm">
                <div className="grid grid-cols-3 gap-2 px-3 py-2">
                  <dt className="text-xs font-medium text-gray-500">
                    Clinician
                  </dt>
                  <dd className="col-span-2 text-xs text-gray-900">
                    {appointment.clinician
                      ? `${appointment.clinician.firstName} ${appointment.clinician.lastName}`
                      : "Clinician info unavailable"}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50">
                  <dt className="text-xs font-medium text-gray-500">Client</dt>
                  <dd className="col-span-2 text-xs text-gray-900">
                    {appointment.client
                      ? `${appointment.client.firstName} ${appointment.client.lastName}`
                      : "Client info unavailable"}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2 px-3 py-2">
                  <dt className="text-xs font-medium text-gray-500">Service</dt>
                  <dd className="col-span-2 text-xs text-gray-900">
                    {appointment.service?.description ||
                      "Service info unavailable"}
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Invoice Details */}
          {Array.isArray(invoices) && invoices.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Invoice Information
                </h3>
              </div>

              {invoices.map((invoice) => (
                <div
                  key={invoice._id}
                  onClick={() => handleInvoiceClick(invoice)}
                  className="border border-gray-200 bg-white shadow-sm mb-3 cursor-pointer hover:border-gray-300 transition-colors hover:ring-2 hover:ring-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500 rounded-sm">
                  <div className="divide-y divide-gray-100">
                    <div className="grid grid-cols-3 gap-2 px-3 py-2">
                      <dt className="text-xs font-medium text-gray-500">
                        Invoice ID
                      </dt>
                      <dd className="col-span-2 text-xs text-gray-900">
                        {invoice.invoiceId || "N/A"}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50">
                      <dt className="text-xs font-medium text-gray-500">
                        Invoice Amount
                      </dt>
                      <dd className="col-span-2 text-xs text-gray-900">
                        ${invoice.amount?.toFixed(2) || "0.00"}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3 gap-2 px-3 py-2">
                      <dt className="text-xs font-medium text-gray-500">
                        Status
                      </dt>
                      <dd className="col-span-2 text-xs text-gray-900">
                        {invoice.status || "N/A"}
                      </dd>
                    </div>
                    {invoice.services && invoice.services.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50">
                        <dt className="text-xs font-medium text-gray-500">
                          Services
                        </dt>
                        <dd className="col-span-2 text-xs text-gray-900">
                          {invoice.services
                            .map((service) => service.description)
                            .join(", ")}
                        </dd>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {showInvoiceGenerator && selectedInvoice && (
                <InvoiceGenerator data={selectedInvoice} />
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500 py-2">
              No invoices available.
            </div>
          )}
        </dl>
      </div>

      {/* Modals */}
      {isWideSidePanelOpen && (
        <AppointmentSummary
          title="Appointment Summary"
          open={isWideSidePanelOpen}
          onClose={handleCloseWideSidePanel}
          appointment={appointment}
        />
      )}

      {!isCompleted && (
        <div className="mt-4 flex items-center justify-end gap-2 text-xs text-gray-500">
          <p>Payrolls will automatically save every hour.</p>
        </div>
      )}
    </div>
  );
}

export default PayrollDetails;
